import { useEffect, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useDebounce, useIntersectionObserver } from '@uidotdev/usehooks'
import { AxiosError } from 'axios'
import { OctagonAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { OrderBy, useOrderBy } from '@/common/hooks/useOrderBy'
import { PostModel } from '@/common/models/post'
import { api, ApiError, APIPaginated } from '@/lib/axios'

type getPaginateNewsProps = {
  userId: string
  page: number
  orderBy: OrderBy
}

async function getPaginateNews({
  userId,
  page,
  orderBy,
}: getPaginateNewsProps) {
  const { data } = await api.backend.get<APIPaginated<PostModel>>(
    '/posts/paginate',
    {
      params: {
        limit: 15,
        page,
        relations: {
          where: {
            isSlider: false,
            isActive: true,
          },
          orderBy,
          include: {
            PostsPinned: {
              where: {
                pinnedById: userId,
              },
            },
          },
        },
      },
    },
  )

  return data
}

export function useNews() {
  const [search, setSearch] = useState('')
  const [searchResult, setSearchResult] = useState<PostModel[]>([])

  const debouncedSearch = useDebounce(search, 300)
  const { userInfo } = useGetUserInfo()
  const { orderBy, handleChangeOrder } = useOrderBy({
    createdAt: 'desc',
  })
  const { t } = useTranslation('panel')
  const [ref, entry] = useIntersectionObserver({
    threshold: 0.5,
  })

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<APIPaginated<PostModel>, AxiosError<ApiError>>({
    queryKey: ['/posts/paginate', userInfo?.id, orderBy],
    queryFn: ({ pageParam }) =>
      getPaginateNews({
        userId: userInfo?.id ?? '',
        page: pageParam as number,
        orderBy,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.meta.currentPage + 1
      return nextPage <= lastPage.meta.totalPages ? nextPage : undefined
    },
    getPreviousPageParam: (firstPage) => {
      const previousPage = firstPage.meta.currentPage - 1
      return previousPage >= 1 ? previousPage : undefined
    },
  })

  const handleNewsSearch = (search: string) => {
    setSearch(search?.toLowerCase() || '')
  }

  useEffect(() => {
    if (error) {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: OctagonAlert,
          title: t('news.use_paginate_posts.error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('news.use_paginate_posts.error.description'),
        },
      })
    }
  }, [error, t])

  useEffect(() => {
    if (!data || data.pages.length === 0) return

    const sanitizedSearch = debouncedSearch.trim().toLowerCase()

    const fields = ['title', 'subTitle', 'fileName', 'postExternalUrl', 'tag']

    const searchedData = data.pages
      .flatMap((page) => page.items)
      .filter((post) =>
        fields.some((field) => {
          const value = post[field as keyof PostModel]

          return (
            typeof value === 'string' &&
            value.toLowerCase().includes(sanitizedSearch)
          )
        }),
      )

    setSearchResult(searchedData)
  }, [data, debouncedSearch, t])

  useEffect(() => {
    if (!entry?.isIntersecting) return

    if (hasNextPage) fetchNextPage()
  }, [entry, hasNextPage, fetchNextPage])

  return {
    news: debouncedSearch
      ? searchResult
      : data?.pages.flatMap((page) => page.items) || [],
    isLoadingNews: isLoading,
    newsOrderBy: orderBy,
    hasMoreNews: hasNextPage,
    hasPreviousNews: hasPreviousPage,
    loadingMoreNews: isFetchingNextPage,
    endElementRef: ref,
    loadMoreNews: fetchNextPage,
    handleChangeOrder,
    handleNewsSearch,
  }
}
